import {is, Set, Map} from 'immutable';
import {getDynamoBindingPaths} from '@packages/systems/dynamo/wfdl';
import {weakMemo} from '@packages/utilities/memo';
import {
  EElement,
  Expression,
  ExpressionElement,
} from '@packages/systems/wfdl-ast';
import {concat, constant, emptyArray} from '@packages/utilities/fp/utils';
import {
  isAnyDynListItemElement,
  isDynamicBoundOrConditioned,
  isDynamoWrapper,
} from '@packages/utilities/elementTypeUtils';
import {
  EvalEnv,
  ExpressionPath,
  Expressions,
  matchExpression,
} from '@packages/systems/wfdl';
import {pair} from '@packages/utilities/Pair';
import {memoFromJS} from '@packages/systems/core/utils/ImmUtils';
import {isEqual} from 'lodash';

const DYN_DATA_PATH: ExpressionPath = [
  {
    in: 'Record',
    at: 'dyn',
  },
];
const DYN_QUERY_EXPR_PATH: ExpressionPath = [
  {in: 'Record', at: 'dyn'},
  {in: 'Record', at: 'query'},
];
const PAGINATION_LIMIT_PATH: ExpressionPath = [
  {in: 'Record', at: 'pagination'},
  {in: 'Record', at: 'limit'},
];

const containsDynBoundOrConditioned = Expressions.some(
  matchExpression({
    Element: isDynamicBoundOrConditioned,
    _: constant(false),
  })
);

export const dynamoWrapperElementHasDynBindingOrCondition = (
  element: ExpressionElement
) => {
  return containsDynBoundOrConditioned(EElement(element));
};

export const dynamoWrapperElementDynDataChanged = weakMemo(
  (prevElement: ExpressionElement) =>
    weakMemo((prevEnv: EvalEnv.EvalEnvType) =>
      weakMemo((nextElement: ExpressionElement) =>
        weakMemo((nextEnv: EvalEnv.EvalEnvType) => {
          const prevWrapperElemDynData =
            getWrapperElementDynData(prevElement)(prevEnv);

          const nextWrapperElemDynData =
            getWrapperElementDynData(nextElement)(nextEnv);
          const result = !is(
            prevWrapperElemDynData.dynDataInfoMap,
            nextWrapperElemDynData.dynDataInfoMap
          );

          if (result) {
            return true;
          }
          const prevEvaluatedDataMap = prevWrapperElemDynData.evaluatedDataMap;
          const nextEvaluatedDataMap = nextWrapperElemDynData.evaluatedDataMap;
          return prevEvaluatedDataMap.some(
            (value, key) => !isEqual(value, nextEvaluatedDataMap.get(key))
          );
        })
      )
    )
);

const getWrapperElementDynData = weakMemo((wrapperElement: ExpressionElement) =>
  weakMemo((evalEnv: EvalEnv.EvalEnvType) => {
    return buildExpressionElementDynDataMap(wrapperElement, evalEnv);
  })
);
type Context = {rootElementId: string};
type DynDataInfo = Map<string, unknown>;
type TreeDynDataInfoMap = Map<string, Set<DynDataInfo>>;
type TreeDynResult = {
  dynDataInfoMap: TreeDynDataInfoMap;
  evaluatedDataMap: Map<string, unknown>;
};
type DynDataInfoArray = Array<{
  rootElementId: string;
  dynDataInfo: DynDataInfo;
  evaluatedData: unknown;
}>;

const defaultMatcher = constant(
  (context: Context): [Context, DynDataInfoArray] => {
    return pair(context, emptyArray);
  }
);

function buildExpressionElementDynDataMap(
  wrapperElement: ExpressionElement,
  evalEnv: EvalEnv.EvalEnvType
): TreeDynResult {
  const initialContext = {
    rootElementId: Expressions.getElementId(wrapperElement),
  };

  const dynDataArray: DynDataInfoArray = Expressions.everythingWithContext(
    initialContext
  )(concat)(
    matchExpression({
      Element:
        (element: ExpressionElement) =>
        (context: Context): [Context, DynDataInfoArray] => {
          const elementId = Expressions.getElementId(element);
          const elementData = Expressions.getElementData(element);
          if (isAnyDynListItemElement(element) && element !== wrapperElement) {
            // All the dynamic list item subtrees have separate binding contexts,
            // so we store the detected dynDataInfo under the current list item ID.
            context = {rootElementId: elementId};
          }

          let dynData;
          const expr = Expressions.getIn(
            Expressions.getElementData(element),
            DYN_DATA_PATH
          );
          // It's important that we convert the dyn data into an Immutable.Map because
          // the upstream logic still relies on the Immutable.js Sets to ignore duplicate
          // data.dyn Immutable Maps when comparing the dynamic data being used in the
          // Collection list subtree.
          if (expr) {
            dynData = memoFromJS(expr);
          } else {
            dynData = null;
          }
          const dynamoUniversalBindingPaths =
            getDynamoBindingPaths(elementData);

          const evaluatedData = getEvaluatedDataFromWrapper(
            element,
            elementData,
            evalEnv
          );

          if (
            !dynData &&
            dynamoUniversalBindingPaths.length === 0 &&
            !evaluatedData
          ) {
            return pair(context, emptyArray);
          }

          const bindingPathsSet = Set().withMutations((set) => {
            dynamoUniversalBindingPaths.forEach((path) => {
              set.add(path.join('.'));
            });
          });

          return pair(context, [
            {
              rootElementId: context.rootElementId,
              dynDataInfo: Map({
                dynData,
                universalBindings: bindingPathsSet,
              }),
              evaluatedData,
            },
          ]);
        },
      _: defaultMatcher,
    })
  )(EElement(wrapperElement));

  const result = Map({
    dynDataInfoMap: Map() as TreeDynDataInfoMap,
    evaluatedDataMap: Map() as Map<string, unknown>,
  }).withMutations((mutableResult) => {
    dynDataArray.forEach(({rootElementId, dynDataInfo, evaluatedData}) => {
      // Update dynDataInfoMap
      mutableResult.updateIn(
        ['dynDataInfoMap', rootElementId],
        (dynDataInfoSet = Set()) => {
          return dynDataInfoSet.add(dynDataInfo);
        }
      );

      // Update evaluatedDataMap if needed
      if (evaluatedData != null) {
        mutableResult.setIn(['evaluatedDataMap', rootElementId], evaluatedData);
      }
    });
  });

  return {
    dynDataInfoMap: result.get('dynDataInfoMap') as TreeDynDataInfoMap,
    evaluatedDataMap: result.get('evaluatedDataMap') as Map<string, unknown>,
  };
}

function getEvaluatedDataFromWrapper(
  element: ExpressionElement,
  elementData: Expression,
  evalEnv: EvalEnv.EvalEnvType
): unknown {
  if (!isDynamoWrapper(element)) {
    return null;
  }
  const dynQueryData = Expressions.getIn(elementData, DYN_QUERY_EXPR_PATH);
  const hasProps = dynQueryData ? hasExpressionProps(dynQueryData) : false;
  const evaluatedDynQuery =
    dynQueryData && hasProps
      ? Expressions.evaluate(evalEnv, dynQueryData)
      : null;
  const paginationLimitData = Expressions.getIn(
    elementData,
    PAGINATION_LIMIT_PATH
  );
  const hasPaginationLimitProps = paginationLimitData
    ? hasExpressionProps(paginationLimitData)
    : false;
  const evaluatedPaginationLimit =
    paginationLimitData && hasPaginationLimitProps
      ? Expressions.evaluate(evalEnv, paginationLimitData)
      : null;
  return {
    dynQuery: evaluatedDynQuery,
    paginationLimit: evaluatedPaginationLimit,
  };
}

const hasExpressionProps = Expressions.some(
  matchExpression({
    Select: () => true,
    _: () => false,
  })
);
